import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FaxData, FaxTabData } from '@dataTypes/message.types'
import {
  FAX_FOLDER_MORE_ITEMS,
  FAX_LABEL_MORE_ITEMS,
  FAX_SIDE_PANEL_SELECTION,
} from '@constants/Message'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useGlobalLoadingActions } from '@hooks'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  setActiveFax,
  setActiveFolder,
  setFaxFilters,
  setSearchQuery,
} from '@store/reducers/fax.reducer'
import {
  createFailedToast,
  createSuccessToast,
  createFaxFolderDeletedToast,
  createFaxFolderRenamedToast,
  createFaxFolderCreatedToast,
} from '@utils/notificationToast.util'
import { RichTextEditorComponent } from '@syncfusion/ej2-react-richtexteditor'
import { FaxTagColor, IFaxFolder, IFaxTag } from '@dataTypes/fax.type'
import {
  addFaxFolder,
  addFaxTag,
  deleteFaxFolder,
  deleteFaxTag,
  fetchAllFax,
  fetchFaxSettings,
  updateFaxFolder,
  updateFaxTag,
} from '@store/actions'
import {
  FaxFilterConfig,
  faxFolderHasUnread,
  faxTagHasUnread,
  getFaxBlockedUsers,
  getFaxFilterConfig,
  getFaxFolderCount,
  getFaxTagCount,
} from '@utils/fax.util'
import { MenuEventArgs } from '@syncfusion/ej2-react-navigations'
import { ItemModel } from '@syncfusion/ej2-react-splitbuttons'

const defaultLabel = {
  clinic_id: '',
  tag_name: '',
  color: FaxTagColor.Default,
}

const defaultFolder = {
  clinic_id: '',
  folder_name: '',
}

const MESSAGE_CENTER_LOADING_CONTAINER = '.message-container-page'

export type FaxFolderWithCount = IFaxFolder & {
  count: number
  hasUnread: boolean
}
export type FaxTagWithCount = IFaxTag & {
  count: number
  hasUnread: boolean
}

export type PendingFaxSideDelete = {
  type: 'folder' | 'label'
  id: string
  name: string
}

export type FaxHooks = {
  activeTab: string
  activeFolder: string
  cancelAddFolder: () => void
  cancelAddLabel: () => void
  cancelEditFolder: () => void
  cancelEditLabel: () => void
  confirmPendingDelete: () => void
  editingFolderId: string | null
  editingFolderName: string
  editingLabel: IFaxTag | null
  editingLabelId: string | null
  faxFilters: Record<string, string[]>
  faxRteRef: RefObject<RichTextEditorComponent>
  filterConfig: FaxFilterConfig
  folderMoreItems: ItemModel[]
  folders: FaxFolderWithCount[]
  folderActive: (faxFolderId: string) => boolean
  handleChangeTab: (tab: keyof FaxData) => void
  handleFaxFilterChange: (selectedFilters: Record<string, string[]>) => void
  handleSearch: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleFolderDropdownClose: () => void
  handleFolderDropdownOpen: (faxFolderId: string) => void
  handleFolderMoreOptionSelect: (
    args: MenuEventArgs,
    folder: FaxFolderWithCount,
  ) => void
  handleLabelDropdownClose: () => void
  handleLabelDropdownOpen: (faxTagId: string) => void
  handleLabelMoreOptionSelect: (
    args: MenuEventArgs,
    label: FaxTagWithCount,
  ) => void
  handleMouseEnter: (itemId: string) => void
  handleMouseLeave: (itemId?: string) => void
  handleSelectFolder: (faxFolderId: string) => void
  handleSelectTag: (faxTagId: string) => void
  hidePendingDelete: () => void
  hoveredItem: string
  showFolderMoreOptions: (faxFolderId: string) => boolean
  showLabelMoreOptions: (faxTagId: string) => boolean
  isSystemTabActive: (tab: keyof FaxData) => boolean
  labelMoreItems: ItemModel[]
  labels: FaxTagWithCount[]
  pendingDelete: PendingFaxSideDelete | null
  saveEditFolder: () => void
  saveEditLabel: () => void
  tagActive: (faxTagId: string) => boolean
  newFolder: IFaxFolder
  newLabel: IFaxTag
  onComposeFax: () => void
  saveFolder: () => void
  saveLabel: () => void
  searchQuery: string
  setEditingFolderName: Dispatch<SetStateAction<string>>
  showAddFolder: boolean
  showAddLabel: boolean
  showFolders: boolean
  showLabels: boolean
  sidePanelSelection: FaxTabData[][]
  toggleShowAddItem: (name: string) => void
  toggleShowItems: (name: string) => void
  updateEditLabel: (key: string, value: string) => void
  updateLabel: (key: string, value: string) => void
  updateFolder: (key: string, value: string) => void
}

function useFax(): FaxHooks {
  const dispatch = useAppDispatch()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const faxRteRef = useRef<RichTextEditorComponent>(null)
  const hoverDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const activeTab = useAppSelector((state) => state.fax.activeFolder)
  const faxState = useAppSelector((state) => state.fax)
  const rawLabels = useAppSelector((state) => state.fax.tags)
  const rawFolders = useAppSelector((state) => state.fax.folders)
  const { activeFolder, searchQuery, faxFilters } = useAppSelector(
    (state) => state.fax,
  )
  const blockedUsers = useAppSelector((state) => state.blockedUser.data)
  const filterConfig = useMemo(
    () => getFaxFilterConfig(activeFolder),
    [activeFolder],
  )
  const faxBlockedUsers = useMemo(
    () => getFaxBlockedUsers(blockedUsers),
    [blockedUsers],
  )
  const [showFolders, setShowFolders] = useState<boolean>(true)
  const [showLabels, setShowLabels] = useState<boolean>(true)
  const [showAddFolder, setShowAddFolder] = useState<boolean>(false)
  const [showAddLabel, setShowAddLabel] = useState<boolean>(false)
  const [newFolder, setNewFolder] = useState<IFaxFolder>(defaultFolder)
  const [newLabel, setNewLabel] = useState<IFaxTag>(defaultLabel)
  const [hoveredItem, setHoveredItem] = useState<string>('')
  const [openFolderMenuId, setOpenFolderMenuId] = useState<string | null>(null)
  const [openLabelMenuId, setOpenLabelMenuId] = useState<string | null>(null)
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null)
  const [editingFolderName, setEditingFolderName] = useState('')
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null)
  const [editingLabel, setEditingLabel] = useState<IFaxTag | null>(null)
  const [pendingDelete, setPendingDelete] =
    useState<PendingFaxSideDelete | null>(null)

  const folderMoreItems = useMemo(
    () => [...FAX_FOLDER_MORE_ITEMS] as ItemModel[],
    [],
  )
  const labelMoreItems = useMemo(
    () => [...FAX_LABEL_MORE_ITEMS] as ItemModel[],
    [],
  )

  const folders = useMemo(
    () =>
      [...rawFolders]
        .sort(
          (a, b) =>
            new Date(b.date_time_created_at ?? 0).getTime() -
            new Date(a.date_time_created_at ?? 0).getTime(),
        )
        .map((folder) => {
          const folderId = folder.fax_folder_id ?? ''
          return {
            ...folder,
            count: getFaxFolderCount(faxState.data, folderId),
            hasUnread: faxFolderHasUnread(faxState.data, folderId),
          }
        }),
    [rawFolders, faxState.data],
  )

  const labels = useMemo(
    () =>
      [...rawLabels]
        .sort(
          (a, b) =>
            new Date(b.date_time_created_at ?? 0).getTime() -
            new Date(a.date_time_created_at ?? 0).getTime(),
        )
        .map((label) => {
          const tagId = label.fax_tag_id ?? ''
          return {
            ...label,
            count: getFaxTagCount(faxState.data, tagId),
            hasUnread: faxTagHasUnread(faxState.data, tagId),
          }
        }),
    [rawLabels, faxState.data],
  )

  const sidePanelSelection = useMemo(
    () =>
      FAX_SIDE_PANEL_SELECTION.map((data) => {
        const items: FaxTabData[] = []
        data.forEach((item) => {
          let count = 0
          let hasUnread = false
          if (item.key === 'blocked') {
            count = faxBlockedUsers.length
          } else if (faxState.counts && item.key in faxState.counts) {
            count = faxState.counts[item.key] ?? 0
          } else {
            count = faxState[item.key as keyof FaxData].length
          }
          if (item.key !== 'blocked') {
            const list = faxState[item.key as keyof FaxData]
            hasUnread = Array.isArray(list) && list.some((fax) => !fax.is_read)
          }
          items.push({
            ...item,
            count,
            hasUnread,
          })
        })
        return items
      }),
    [faxState, faxBlockedUsers],
  )

  const onComposeFax = useCallback((): void => {
    dispatch(setActiveFax(null))
    dispatch(setActiveFolder('compose'))
  }, [dispatch])

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      dispatch(setSearchQuery(event.target.value))
    },
    [dispatch],
  )

  const handleFaxFilterChange = useCallback(
    (selectedFilters: Record<string, string[]>): void => {
      dispatch(setFaxFilters(selectedFilters))
    },
    [dispatch],
  )

  const isSystemTabActive = (tab: keyof FaxData): boolean => activeTab === tab

  const folderActive = (faxFolderId: string): boolean =>
    activeTab === `folder:${faxFolderId}`

  const tagActive = (faxTagId: string): boolean =>
    activeTab === `tag:${faxTagId}`

  const handleChangeTab = (tab: keyof FaxData): void => {
    dispatch(setActiveFolder(tab))
  }

  const handleSelectFolder = (faxFolderId: string): void => {
    dispatch(setActiveFolder(`folder:${faxFolderId}`))
  }

  const handleSelectTag = (faxTagId: string): void => {
    dispatch(setActiveFolder(`tag:${faxTagId}`))
  }

  const handleFolderDropdownOpen = useCallback((faxFolderId: string): void => {
    setOpenFolderMenuId(faxFolderId)
    setOpenLabelMenuId(null)
  }, [])

  const handleFolderDropdownClose = useCallback((): void => {
    setOpenFolderMenuId(null)
    if (hoverDebounceRef.current) {
      clearTimeout(hoverDebounceRef.current)
    }
    hoverDebounceRef.current = setTimeout(() => {
      setHoveredItem('')
    }, 100)
  }, [])

  const handleLabelDropdownOpen = useCallback((faxTagId: string): void => {
    setOpenLabelMenuId(faxTagId)
    setOpenFolderMenuId(null)
  }, [])

  const handleLabelDropdownClose = useCallback((): void => {
    setOpenLabelMenuId(null)
    if (hoverDebounceRef.current) {
      clearTimeout(hoverDebounceRef.current)
    }
    hoverDebounceRef.current = setTimeout(() => {
      setHoveredItem('')
    }, 100)
  }, [])

  const handleMouseEnter = (itemId: string): void => {
    if (hoverDebounceRef.current) {
      clearTimeout(hoverDebounceRef.current)
    }
    hoverDebounceRef.current = setTimeout(() => {
      setHoveredItem(itemId)
    }, 50)
  }

  const handleMouseLeave = (itemId?: string): void => {
    if (itemId && openFolderMenuId === itemId) return
    if (itemId && openLabelMenuId === itemId) return
    if (hoverDebounceRef.current) {
      clearTimeout(hoverDebounceRef.current)
    }
    hoverDebounceRef.current = setTimeout(() => {
      const folderMenuBlocksClear =
        openFolderMenuId && (!itemId || openFolderMenuId === itemId)
      const labelMenuBlocksClear =
        openLabelMenuId && (!itemId || openLabelMenuId === itemId)
      if (!folderMenuBlocksClear && !labelMenuBlocksClear) {
        setHoveredItem('')
      }
    }, 50)
  }

  const showFolderMoreOptions = (faxFolderId: string): boolean =>
    hoveredItem === faxFolderId || openFolderMenuId === faxFolderId

  const showLabelMoreOptions = (faxTagId: string): boolean =>
    hoveredItem === faxTagId || openLabelMenuId === faxTagId

  const cancelEditFolder = (): void => {
    setEditingFolderId(null)
    setEditingFolderName('')
  }

  const cancelEditLabel = (): void => {
    setEditingLabelId(null)
    setEditingLabel(null)
  }

  const hidePendingDelete = (): void => {
    setPendingDelete(null)
  }

  const handleStartRenameFolder = (folder: IFaxFolder): void => {
    if (!folder.fax_folder_id) return
    setShowAddFolder(false)
    setEditingFolderId(folder.fax_folder_id)
    setEditingFolderName(folder.folder_name)
  }

  const handleStartEditLabel = (label: IFaxTag): void => {
    if (!label.fax_tag_id) return
    setShowAddLabel(false)
    setEditingLabelId(label.fax_tag_id)
    setEditingLabel({ ...label })
  }

  const handleRequestDeleteFolder = (folder: IFaxFolder): void => {
    if (!folder.fax_folder_id) return
    setPendingDelete({
      type: 'folder',
      id: folder.fax_folder_id,
      name: folder.folder_name,
    })
  }

  const handleRequestDeleteLabel = (label: IFaxTag): void => {
    if (!label.fax_tag_id) return
    setPendingDelete({
      type: 'label',
      id: label.fax_tag_id,
      name: label.tag_name,
    })
  }

  const handleFolderMoreOptionSelect = (
    args: MenuEventArgs,
    folder: FaxFolderWithCount,
  ): void => {
    handleFolderDropdownClose()
    const actionId = args.item.id ?? ''
    if (actionId === 'rename') {
      handleStartRenameFolder(folder)
    } else if (actionId === 'delete') {
      handleRequestDeleteFolder(folder)
    }
  }

  const handleLabelMoreOptionSelect = (
    args: MenuEventArgs,
    label: FaxTagWithCount,
  ): void => {
    handleLabelDropdownClose()
    const actionId = args.item.id ?? ''
    if (actionId === 'edit') {
      handleStartEditLabel(label)
    } else if (actionId === 'delete') {
      handleRequestDeleteLabel(label)
    }
  }

  const confirmPendingDelete = async (): Promise<void> => {
    if (!pendingDelete) return
    const { type, id, name } = pendingDelete
    setPendingDelete(null)

    showLoading({ container: MESSAGE_CENTER_LOADING_CONTAINER })
    try {
      if (type === 'folder') {
        await dispatch(deleteFaxFolder(id)).unwrap()
        await dispatch(fetchAllFax()).unwrap()
        if (activeTab === `folder:${id}`) {
          dispatch(setActiveFolder('inbox'))
        }
        if (editingFolderId === id) {
          cancelEditFolder()
        }
        showToast(createFaxFolderDeletedToast(name))
      } else {
        await dispatch(deleteFaxTag(id)).unwrap()
        await dispatch(fetchAllFax()).unwrap()
        if (activeTab === `tag:${id}`) {
          dispatch(setActiveFolder('inbox'))
        }
        if (editingLabelId === id) {
          cancelEditLabel()
        }
        const quotedName = name.includes('"') ? name : `"${name}"`
        showToast(
          createSuccessToast(
            'Delete label',
            `${quotedName} fax label has been deleted.`,
          ),
        )
      }
    } catch {
      showToast(
        createFailedToast(
          type === 'folder' ? 'Delete folder failed' : 'Delete label failed',
          type === 'folder'
            ? 'Could not delete folder. Please try again.'
            : 'Could not delete label. Please try again.',
        ),
      )
    } finally {
      hideLoading(MESSAGE_CENTER_LOADING_CONTAINER)
    }
  }

  const saveEditFolder = async (): Promise<void> => {
    if (!editingFolderId || editingFolderName.trim() === '') return
    const folderId = editingFolderId
    const folderName = editingFolderName.trim()

    showLoading({ container: MESSAGE_CENTER_LOADING_CONTAINER })
    try {
      await dispatch(
        updateFaxFolder({
          fax_folder_id: folderId,
          data: { folder_name: folderName },
        }),
      ).unwrap()
      cancelEditFolder()
      showToast(createFaxFolderRenamedToast(folderName))
    } catch {
      showToast(
        createFailedToast(
          'Rename failed',
          'Could not rename folder. Please try again.',
        ),
      )
    } finally {
      hideLoading(MESSAGE_CENTER_LOADING_CONTAINER)
    }
  }

  const saveEditLabel = async (): Promise<void> => {
    if (
      !editingLabelId ||
      !editingLabel ||
      editingLabel.tag_name.trim() === ''
    ) {
      return
    }
    const tagId = editingLabelId
    const tagName = editingLabel.tag_name.trim()
    const tagColor = editingLabel.color

    showLoading({ container: MESSAGE_CENTER_LOADING_CONTAINER })
    try {
      await dispatch(
        updateFaxTag({
          fax_tag_id: tagId,
          data: {
            tag_name: tagName,
            color: tagColor,
          },
        }),
      ).unwrap()
      cancelEditLabel()
      showToast(
        createSuccessToast(
          'Update label',
          `Fax label "${tagName}" has been updated .`,
        ),
      )
    } catch {
      showToast(
        createFailedToast(
          'Update failed',
          'Could not update label. Please try again.',
        ),
      )
    } finally {
      hideLoading(MESSAGE_CENTER_LOADING_CONTAINER)
    }
  }

  const updateEditLabel = (key: string, value: string): void => {
    setEditingLabel((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  const toggleShowItems = (name: string): void => {
    if (name === 'folder') {
      setShowFolders((prev) => !prev)
    } else if (name === 'label') {
      setShowLabels((prev) => !prev)
    }
  }

  const toggleShowAddItem = (name: string): void => {
    if (name === 'folder') {
      setShowAddFolder(true)
      setShowAddLabel(false)
      cancelEditFolder()
    } else if (name === 'label') {
      setShowAddLabel(true)
      setShowAddFolder(false)
      cancelEditLabel()
    }
  }

  const updateLabel = (key: string, value: string): void => {
    setNewLabel((prev) => ({ ...prev, [key]: value }))
  }

  const updateFolder = (key: string, value: string): void => {
    setNewFolder((prev) => ({ ...prev, [key]: value }))
  }

  const cancelAddLabel = (): void => {
    setNewLabel(defaultLabel)
    setShowAddLabel(false)
  }

  const cancelAddFolder = (): void => {
    setNewFolder(defaultFolder)
    setShowAddFolder(false)
  }

  const validateAddItem = (name: string): boolean => {
    if (name === 'folder') {
      return newFolder.folder_name.trim() !== ''
    } else if (name === 'label') {
      return newLabel.tag_name.trim() !== ''
    }
    return false
  }

  const saveLabel = (): void => {
    if (!validateAddItem('label')) {
      return
    }
    dispatch(addFaxTag(newLabel))
    setNewLabel(defaultLabel)
    setShowAddLabel(false)
  }

  const saveFolder = (): void => {
    if (!validateAddItem('folder')) {
      return
    }
    const folderName = newFolder.folder_name.trim()
    dispatch(addFaxFolder(newFolder))
    setNewFolder(defaultFolder)
    setShowAddFolder(false)
    showToast(createFaxFolderCreatedToast(folderName))
  }

  useEffect(() => {
    dispatch(setActiveFolder('inbox'))
    dispatch(fetchFaxSettings())
  }, [])

  useEffect(() => {
    const rte = faxRteRef.current as { destroy?: () => void } | null
    return () => {
      if (rte && typeof rte.destroy === 'function') {
        try {
          rte.destroy()
        } catch {
          console.error('Error destroying RTE instance')
        }
      }
    }
  }, [])

  useEffect(() => {
    const openMenuId = openFolderMenuId ?? openLabelMenuId
    if (!openMenuId) return

    const handleClickOutside = (e: Event): void => {
      const target = e.target as HTMLElement
      const isInsideDropdownPopup =
        target.closest('.e-dropdown-popup') !== null ||
        target.closest('.e-popup') !== null ||
        target.closest('.mcf-side-more-options') !== null ||
        target.closest('.mcf-side-more-options-wrap') !== null

      if (!isInsideDropdownPopup) {
        if (openFolderMenuId) {
          handleFolderDropdownClose()
        } else if (openLabelMenuId) {
          handleLabelDropdownClose()
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [
    openFolderMenuId,
    openLabelMenuId,
    handleFolderDropdownClose,
    handleLabelDropdownClose,
  ])

  return {
    activeTab,
    activeFolder,
    cancelAddFolder,
    cancelAddLabel,
    cancelEditFolder,
    cancelEditLabel,
    confirmPendingDelete,
    editingFolderId,
    editingFolderName,
    editingLabel,
    editingLabelId,
    faxFilters,
    faxRteRef,
    filterConfig,
    folderMoreItems,
    folders,
    folderActive,
    handleChangeTab,
    handleFaxFilterChange,
    handleSearch,
    handleFolderDropdownClose,
    handleFolderDropdownOpen,
    handleFolderMoreOptionSelect,
    handleLabelDropdownClose,
    handleLabelDropdownOpen,
    handleLabelMoreOptionSelect,
    handleMouseEnter,
    handleMouseLeave,
    handleSelectFolder,
    handleSelectTag,
    hidePendingDelete,
    hoveredItem,
    isSystemTabActive,
    labelMoreItems,
    labels,
    pendingDelete,
    saveEditFolder,
    saveEditLabel,
    showFolderMoreOptions,
    showLabelMoreOptions,
    tagActive,
    newFolder,
    newLabel,
    onComposeFax,
    saveFolder,
    saveLabel,
    searchQuery,
    setEditingFolderName,
    showAddFolder,
    showAddLabel,
    showFolders,
    showLabels,
    sidePanelSelection,
    toggleShowAddItem,
    toggleShowItems,
    updateEditLabel,
    updateLabel,
    updateFolder,
  }
}

export default useFax
